<script setup>
import { ref, onMounted, watch } from 'vue'
import axios from 'axios'
import Header from './component/header.vue'

const loading  = ref(false)
const syncing  = ref(false)
const cycles   = ref([])
const logs     = ref({ data: [], current_page: 1, last_page: 1 })
const selCycle = ref('')
const syncType = ref('both')
const page     = ref(1)

const loadCycles = async () => {
  const res = await axios.get('/hr/pms/cycles', { params: { per_page: 100, status: 'finalized' } })
  cycles.value = res.data.data?.data ?? []
}

const loadLogs = async () => {
  try {
    loading.value = true
    const params = { per_page: 20, page: page.value }
    if (selCycle.value) params.cycle_id = selCycle.value
    const res = await axios.get('/hr/pms/sync/logs', { params })
    logs.value = res.data.data
  } catch (e) { console.error(e) } finally { loading.value = false }
}

onMounted(() => { loadCycles(); loadLogs() })
watch([selCycle, page], loadLogs)

const runSync = async () => {
  if (!selCycle.value) { alert('Select a cycle first'); return }
  const cycleName = cycles.value.find(c => c.id === selCycle.value)?.name ?? selCycle.value
  if (!confirm(`Sync payroll for cycle "${cycleName}" (type: ${syncType.value})?`)) return
  try {
    syncing.value = true
    await axios.post(`/hr/pms/sync/cycles/${selCycle.value}`, { sync_type: syncType.value })
    loadLogs()
  } catch (e) {
    alert(e.response?.data?.message ?? 'Sync failed')
  } finally { syncing.value = false }
}

const retry = async (cycleId) => {
  if (!confirm('Retry failed payouts for this cycle?')) return
  await axios.post(`/hr/pms/sync/cycles/${cycleId}/retry`)
  loadLogs()
}

const statusClass = (s) => ({
  success: 'badge--green',
  failed:  'badge--red',
  partial: 'badge--orange',
}[s] ?? '')
</script>

<template>
  <div>
    <Header />
    <div class="pms-page">
      <div class="sync-card">
        <h3 class="sync-title">Run Payroll Sync</h3>
        <div class="sync-fields">
          <div class="form-group">
            <label>Cycle (finalized only)</label>
            <select v-model="selCycle">
              <option value="">— Select cycle —</option>
              <option v-for="c in cycles" :key="c.id" :value="c.id">{{ c.name }}</option>
            </select>
          </div>
          <div class="form-group">
            <label>Sync Type</label>
            <select v-model="syncType">
              <option value="both">Both (Bonus + Increment)</option>
              <option value="bonus">Bonus Only</option>
              <option value="salary_increment">Salary Increment Only</option>
            </select>
          </div>
          <div class="sync-btn-wrap">
            <button class="btn-primary" :disabled="syncing || !selCycle" @click="runSync">
              {{ syncing ? 'Syncing…' : 'Run Sync' }}
            </button>
          </div>
        </div>
      </div>

      <h3 class="section-title">Sync Logs</h3>
      <div class="table-card">
        <div v-if="loading" class="tbl-center">Loading…</div>
        <table v-else class="data-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Cycle</th>
              <th>Type</th>
              <th>Total</th>
              <th>Success</th>
              <th>Failed</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!logs.data?.length"><td colspan="8" class="tbl-center">No sync logs yet</td></tr>
            <tr v-for="l in logs.data" :key="l.id">
              <td>{{ new Date(l.created_at).toLocaleDateString('en-GB') }}</td>
              <td>{{ l.cycle?.name ?? '—' }}</td>
              <td>{{ l.sync_type?.replace('_', ' ') }}</td>
              <td>{{ l.total_records }}</td>
              <td class="bold green">{{ l.success_count }}</td>
              <td class="bold red">{{ l.failed_count }}</td>
              <td><span :class="['badge', statusClass(l.status)]">{{ l.status }}</span></td>
              <td>
                <button v-if="l.failed_count > 0" class="btn-link" @click="retry(l.cycle_id)">Retry Failed</button>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="logs.last_page > 1" class="pagination">
          <button :disabled="page <= 1" @click="page--">‹</button>
          <span>{{ page }} / {{ logs.last_page }}</span>
          <button :disabled="page >= logs.last_page" @click="page++">›</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.pms-page { padding: 0 24px 40px; }
.sync-card { background: #fff; border: 1px solid #eaf0fb; border-radius: 10px; padding: 20px 24px; margin-bottom: 24px; }
.sync-title { font-size: 15px; font-weight: 700; color: #1a2a45; margin: 0 0 16px; }
.sync-fields { display: flex; align-items: flex-end; gap: 16px; flex-wrap: wrap; }
.sync-btn-wrap { padding-bottom: 1px; }
.section-title { font-size: 16px; font-weight: 700; color: #1a2a45; margin-bottom: 14px; }
.form-group { display: flex; flex-direction: column; gap: 5px; }
.form-group label { font-size: 12.5px; font-weight: 600; color: #556; }
.form-group select { padding: 9px 12px; border: 1px solid #dde3ef; border-radius: 6px; font-size: 13.5px; min-width: 200px; }
.table-card { background: #fff; border-radius: 10px; border: 1px solid #eaf0fb; overflow: hidden; }
.data-table { width: 100%; border-collapse: collapse; font-size: 13.5px; }
.data-table th { padding: 12px 14px; text-align: left; background: #f7f9fc; font-weight: 600; color: #556; font-size: 12px; }
.data-table td { padding: 12px 14px; border-top: 1px solid #f0f3fa; }
.tbl-center { text-align: center; color: #999; padding: 40px; }
.bold { font-weight: 600; }
.green { color: #0f5132; }
.red   { color: #842029; }
.pagination { display: flex; align-items: center; gap: 12px; justify-content: center; padding: 12px; }
.pagination button { padding: 5px 12px; border: 1px solid #dde3ef; border-radius: 4px; cursor: pointer; background: #fff; }
.pagination button:disabled { opacity: 0.4; }
.badge { display: inline-block; padding: 3px 10px; border-radius: 999px; font-size: 11.5px; font-weight: 600; text-transform: capitalize; }
.badge--green  { background: #d1e7dd; color: #0f5132; }
.badge--orange { background: #fff3cd; color: #856404; }
.badge--red    { background: #f8d7da; color: #842029; }
.btn-primary { padding: 9px 20px; background: #004AAD; color: #fff; border: none; border-radius: 6px; font-size: 13.5px; font-weight: 600; cursor: pointer; }
.btn-link { background: none; border: none; color: #004AAD; font-size: 13px; cursor: pointer; }
</style>
